/*
 * Christian Kiddo — Shopify mobile drawer / drawing toolbar bridge
 *
 * Drawing exercises use the Popover API. An open popover is placed in the
 * browser's top layer, so no z-index on Shopify's ordinary menu drawer can
 * cover it. This script temporarily closes drawing-toolbar popovers while the
 * main drawer is open and restores only the popovers it closed.
 */
(function () {
  "use strict";

  const MENU_DETAILS_SELECTOR =
    "#Details-menu-drawer-container";

  const TOOLBAR_LAYER_SELECTOR =
    ".ck-toolbar-layer";

  function startMenuDrawerFix() {
    const menuDetails = document.querySelector(
      MENU_DETAILS_SELECTOR
    );

    if (!menuDetails) {
      return;
    }

    const menuSummary = menuDetails.querySelector(
      ":scope > summary"
    );

    /*
     * Map is deliberately used instead of a WeakMap because all suspended
     * layers must be iterated when the drawer closes.
     */
    const suspendedLayers = new Map();

    function isPopoverOpen(layer) {
      try {
        return layer.matches(":popover-open");
      } catch (error) {
        return false;
      }
    }

    function createLayerState(layer) {
      return {
        reopenPopover: false,
        usedDisplayFallback: false,
        displayValue:
          layer.style.getPropertyValue("display"),
        displayPriority:
          layer.style.getPropertyPriority("display"),
        hadAriaHidden:
          layer.hasAttribute("aria-hidden"),
        ariaHiddenValue:
          layer.getAttribute("aria-hidden")
      };
    }

    function markLayerHidden(layer) {
      layer.setAttribute("aria-hidden", "true");
    }

    function restoreLayerAccessibility(layer, state) {
      if (state.hadAriaHidden) {
        layer.setAttribute(
          "aria-hidden",
          state.ariaHiddenValue
        );
      } else {
        layer.removeAttribute("aria-hidden");
      }
    }

    function useDisplayFallback(layer, state) {
      state.usedDisplayFallback = true;

      layer.style.setProperty(
        "display",
        "none",
        "important"
      );
    }

    function suspendToolbarLayer(layer) {
      if (
        !(layer instanceof HTMLElement) ||
        suspendedLayers.has(layer)
      ) {
        return;
      }

      const hasNativePopover =
        layer.hasAttribute("popover") &&
        typeof layer.hidePopover === "function";

      if (hasNativePopover) {
        if (!isPopoverOpen(layer)) {
          return;
        }

        const state = createLayerState(layer);

        state.reopenPopover = true;
        suspendedLayers.set(layer, state);
        markLayerHidden(layer);

        try {
          layer.hidePopover();
        } catch (error) {
          useDisplayFallback(layer, state);
        }

        return;
      }

      /*
       * Fallback for older browsers. In the drawing code the popover
       * attribute is removed when the API is unavailable, leaving an ordinary
       * fixed layer behind. Hide that layer without changing the exercise.
       */
      const state = createLayerState(layer);

      suspendedLayers.set(layer, state);
      markLayerHidden(layer);
      useDisplayFallback(layer, state);
    }

    function suspendAllToolbarLayers() {
      document
        .querySelectorAll(TOOLBAR_LAYER_SELECTOR)
        .forEach(suspendToolbarLayer);
    }

    function restoreToolbarLayers() {
      suspendedLayers.forEach(function (state, layer) {
        suspendedLayers.delete(layer);

        if (!layer.isConnected) {
          return;
        }

        if (state.usedDisplayFallback) {
          if (state.displayValue) {
            layer.style.setProperty(
              "display",
              state.displayValue,
              state.displayPriority
            );
          } else {
            layer.style.removeProperty("display");
          }
        }

        restoreLayerAccessibility(layer, state);

        if (
          state.reopenPopover &&
          layer.hasAttribute("popover") &&
          typeof layer.showPopover === "function" &&
          !isPopoverOpen(layer)
        ) {
          try {
            layer.showPopover();
          } catch (error) {
            /*
             * The exercise may have been removed during course navigation.
             * In that case there is nothing left to restore.
             */
          }
        }
      });

      /* Recalculate floating-toolbar geometry after the drawer animation. */
      window.requestAnimationFrame(function () {
        window.dispatchEvent(new Event("resize"));
      });
    }

    function syncWithDrawer() {
      document.documentElement.classList.toggle(
        "ck-shopify-menu-open",
        menuDetails.open
      );

      if (menuDetails.open) {
        suspendAllToolbarLayers();
      } else {
        restoreToolbarLayers();
      }
    }

    /*
     * Hide before the <details> default click action opens the drawer. This
     * prevents a one-frame flash of the toolbar over the drawer.
     */
    if (menuSummary) {
      menuSummary.addEventListener(
        "click",
        function () {
          if (!menuDetails.open) {
            suspendAllToolbarLayers();

            queueMicrotask(syncWithDrawer);
          }
        },
        true
      );
    }

    /* Handles mouse, keyboard, Escape and Shopify's own drawer controls. */
    const drawerObserver = new MutationObserver(
      syncWithDrawer
    );

    drawerObserver.observe(menuDetails, {
      attributes: true,
      attributeFilter: ["open"]
    });

    menuDetails.addEventListener("toggle", syncWithDrawer);

    /*
     * If an exercise tries to open a toolbar while the drawer is already open,
     * cancel that opening and remember to restore it when the drawer closes.
     */
    document.addEventListener(
      "beforetoggle",
      function (event) {
        const layer = event.target;

        if (
          !menuDetails.open ||
          event.newState !== "open" ||
          !(layer instanceof HTMLElement) ||
          !layer.matches(TOOLBAR_LAYER_SELECTOR)
        ) {
          return;
        }

        if (!suspendedLayers.has(layer)) {
          const state = createLayerState(layer);

          state.reopenPopover = true;
          suspendedLayers.set(layer, state);
          markLayerHidden(layer);
        }

        event.preventDefault();
      },
      true
    );

    /*
     * LDT can inject lesson content after initial page load. Catch newly added
     * toolbar layers and close them immediately if the drawer is open.
     */
    const layerObserver = new MutationObserver(
      function (mutations) {
        if (!menuDetails.open) {
          return;
        }

        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            if (!(node instanceof Element)) {
              return;
            }

            if (node.matches(TOOLBAR_LAYER_SELECTOR)) {
              suspendToolbarLayer(node);
            }

            node
              .querySelectorAll(TOOLBAR_LAYER_SELECTOR)
              .forEach(suspendToolbarLayer);
          });
        });
      }
    );

    layerObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    syncWithDrawer();
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      startMenuDrawerFix,
      { once: true }
    );
  } else {
    startMenuDrawerFix();
  }
})();